<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('wholesaleReceipt.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('wholesaleReceipt.pageTitle'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between ts-items-center ts-whitespace-no-wrap ts-space-x-3">
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker v-model="dateRange" :allowClear="false" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY"
              style="width: 200px" @change="fetchData()" />
            <ts-button color="success" @click.prevent="onExport" :waiting="exporting" class="ml-2">
              <i class="fas fa-file-excel"></i> {{ $t('export') }}
            </ts-button>
          </div>

          <div class="d-flex ts-space-x-3 ts-items-center">
            <label class="mt-2">Status :</label>
            <a-select v-model="approve_status" :options="approveStatusList" style="width: 150px"></a-select>
            <div class="ts-border-gray-200 ts-border ts-rounded ts-p-2 ts-flex ts-items-center">
              <input v-model="searchAllDate" @change="fetchData()" type="checkbox" id="searchAllDate" />
              <label for="searchAllDate" class="ts-mb-0 ts-ml-1">{{
                $t('stockIn.searchAllDate')
              }}</label>
            </div>
            <ts-input v-tooltip="$t('wholesaleReceipt.search')" v-model="search" :placeholder="$t('search')"
              style="width: 250px" class="inline-block" />
            <ts-filter ref="filter">
              <div class="row mb-3">
                <div class="col-md-12">
                  <label class="">{{
                    $t('wholesaleReceipt.branchName')
                  }}</label>
                  <a-select style="width: 257px" v-model="branch_id" :options="branchs" showSearch mode="multiple"
                    :filterOption="(input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                      " :maxTagCount="1" :maxTagTextLength="17" :placeholder="$t('all')" @change="changeBranch()">
                  </a-select>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{
                    $t('wholesaleReceipt.warehourse')
                  }}</label>
                  <a-select style="width: 257px" v-model="location_id" :options="locations" showSearch mode="multiple"
                    :filterOption="(input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                      " :maxTagCount="1" :maxTagTextLength="17" :placeholder="$t('all')"></a-select>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table :columns="columns" :records="resources" :loading="loading" :rowGroup="{
          field: 'payment_date'
        }" :expandable="true">
          <template v-slot="{ record }">
            <td>{{ record.branch.branch_name_en }}</td>
            <td class="ts-whitespace-no-wrap">
              {{ record.receipt_no }}
            </td>
            <td class="ts-whitespace-no-wrap">
              {{ record.customer_name }}
            </td>

            <td class="ts-whitespace-no-wrap ts-text-right">
              {{ totalInvoice(record) }}
            </td>
            <td class="ts-whitespace-no-wrap ts-text-right">
              {{ totalAmountPaid(record) }}
            </td>
            <td class="ts-whitespace-no-wrap ts-text-right">
              {{ totalAmountDue(record) }}
            </td>
            <td class="ts-whitespace-no-wrap ts-text-center">
              {{ record.cashier_name }}
            </td>
            <td class="ts-whitespace-no-wrap ts-text-center">
              <span class="badge badge-warning" v-if="record.approve_status == 0">Pending</span>
              <span class="badge badge-success" v-if="record.approve_status == 2">Approved</span>
              <span class="badge badge-danger" v-if="record.approve_status == 3">Rejeceted</span>
            </td>
            <td class="ts-whitespace-no-wrap ts-text-center">
              {{ record.approved_by_name ? record.approved_by_name : '' }}
            </td>
            <td class="text-center ts-space-x-2 ts-whitespace-no-wrap">
              <a v-if="record.approve_status == 0" href="#" v-tooltip="'Confirm'" class="ml-2 text-success"
                @click.prevent="confirm(record, 2)">
                <i class="fas fa-check"></i>
              </a>
              <a v-if="record.approve_status == 0" href="#" v-tooltip="'Reject'" class="ml-2 text-danger"
                @click.prevent="confirm(record, 3)">
                <i class="fas fa-times"></i>
              </a>
              <a href="#" v-tooltip="$t('quotation.reprint')" class="text-primary" @click.prevent="reprintReport(record)">
                <i class="fas fa-print"></i>
              </a>
              <a-popconfirm placement="leftTop" :title="$t('are_you_sure_to_delete')" :ok-text="$t('yes')"
                :cancel-text="$t('no')" @confirm="deleteRecord(record)">
                <a href="#" :disabled="record._deleting">
                  <i class="fas fa-circle-notch spin" v-if="record._deleting"></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
            </td>
          </template>
          <div slot="expand" slot-scope="prop">
            <table class="ts-w-11/12 ts-mx-auto detail">
              <thead>
                <tr class="ts-uppercase ts-bg-gray-200 ts-h-8 ts-text-white">
                  <th class="ts-pl-2 ts-whitespace-nowrap">
                    {{ $t('wholesaleReceipt.invoiceNo') }}
                  </th>
                  <th class="ts-pl-2 ts-whitespace-nowrap">
                    {{ $t('wholesaleReceipt.customer') }}
                  </th>
                  <th class="ts-pl-2 ts-whitespace-nowrap ts-text-right">
                    {{ $t('wholesaleReceipt.invoiceAmount') }}
                  </th>
                  <th class="ts-pl-2 ts-whitespace-nowrap ts-text-right">
                    {{ $t('wholesaleReceipt.amountPaid') }}
                  </th>
                  <th class="ts-pl-2 ts-whitespace-nowrap ts-text-right">
                    {{ $t('wholesaleReceipt.amountDue') }}
                  </th>
                  <th class="ts-pl-2 ts-whitespace-nowrap ts-text-center">
                    {{ $t('wholesaleReceipt.orgNextPayment') }}
                  </th>
                  <th class="ts-pl-2 ts-whitespace-nowrap ts-text-center">
                    {{ $t('wholesaleReceipt.nextPayment') }}
                  </th>
                  <th class="ts-pl-2 ts-whitespace-nowrap">
                    {{ $t('wholesaleReceipt.paymentStatus') }}
                  </th>
                  <th class="ts-pl-2 ts-whitespace-nowrap">
                    {{ $t('wholesaleReceipt.saleName') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr class="ts-border-b-2" v-for="(detail, index) in prop.record.children" :key="index">
                  <td class="ts-p-2 ts-whitespace-nowrap">
                    {{ detail.invoice_no }}
                  </td>
                  <td class="ts-p-2 ts-whitespace-nowrap">
                    {{ detail.customer_name }}
                  </td>
                  <td class="ts-p-2 ts-whitespace-nowrap ts-text-right">
                    {{ $defaultCurrencyWithCode(parseFloat(detail.invoice_amount)) }}
                  </td>
                  <td class="ts-p-2 ts-whitespace-nowrap ts-text-right">
                    {{ $defaultCurrencyWithCode(parseFloat(detail.amount_paid)) }}
                  </td>
                  <td class="ts-p-2 ts-whitespace-nowrap ts-text-right">
                    {{
                      detail.amount_due
                      ? $defaultCurrencyWithCode(parseFloat(detail.amount_due))
                      : + $defaultCurrencyWithCode(parseFloat(detail.amount_due).toFixed(2))
                    }}
                  </td>
                  <td class="ts-p-2 ts-whitespace-nowrap ts-text-center">
                    <span :class="detail.org_payment_date
                          ? 'ts-px-3 ts-rounded-md ts-bg-pink-600 ts-text-white ts-font-bold'
                          : ''
                        ">{{ orgPaymentDate(detail.org_payment_date) }}</span>
                  </td>
                  <td class="ts-p-2 ts-whitespace-nowrap ts-text-center">
                    <span :class="detail.next_payment_date
                      ? 'ts-px-3 ts-rounded-md ts-bg-pink-600 ts-text-white ts-font-bold'
                      : ''
                      ">{{ detail.next_payment_date }}</span>
                  </td>
                  <td class="ts-p-2 ts-whitespace-nowrap">
                    {{ detail.payment_status }}
                  </td>
                  <td class="ts-p-2 ts-whitespace-nowrap">
                    {{ detail.sale_name }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
      <a-modal v-if="wholesalereceipt" v-model="wholesalereceipt" :title="$t('wholesaleReceipt.receipt')" :footer="null"
        width="60%" @cancel="onCancel">
        <Receipt :record="receipt_data" @cancel="onCancel" />
      </a-modal>
      <a-modal :title="$t('pdf')" width="50%" v-model="show_pdf" :footer="null">
        <ts-preview-pdf v-model="waiting" :src="src" />
      </a-modal>
      <a-modal v-if="showForm" v-model="showForm" :title="confirm_type == 2
        ? $t('wholesaleReceipt.are_you_sure_to_approve')
        : $t('wholesaleReceipt.are_you_sure_to_reject')
        " centered width="400px" :zIndex="1020" :footer="null">
        <confirm :validate="errors" @cancel="onCancel" v-model="confirmData" :confirm_type="confirm_type" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import Receipt from './components/payment-receipt'
import Reason from './components/reason_delete.vue'
import Confirm from './components/confirm.vue'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'
import moment from 'moment'

export default {
  name: 'wholesaleReceipt',
  components: {
    Receipt,
    Confirm
  },
  data() {
    return {
      loading: false,
      waiting: false,
      showForm: false,
      exporting: false,
      src: null,
      receipt_data: {},
      wholesalereceipt: false,
      show_export_form: false,
      show_pdf: false,
      confirm_type: null,
      approveStatusList: [
        { value: '', label: 'All' },
        { value: '0', label: 'Pendding' },
        { value: '2', label: 'Approve' },
        { value: '3', label: 'Reject' }
      ],
      confirmData: {},
      model: {
        deleted_reason: null
      },
      errors: new Errors(),
      locations: [],
      branchs: []
    }
  },
  created() {
    this.branch_id = [this.$store.state.authUser.user.default_branch.branch_id]
    this.getBranch()
    this.changeBranch()
    this.location_id = [
      this.$store.state.authUser.user.default_location.location_id
    ]
  },
  computed: {
    ...mapState('wholesale/receipt', ['resources', 'pagination']),

    search: {
      get() {
        return this.$store.state.wholesale.receipt.search
      },
      set(newValue) {
        this.$store.commit('wholesale/receipt/SET_SEARCH', newValue)
        this.$store.commit('wholesale/receipt/RESET_CURRENT_PAGE')
      }
    },
    dateRange: {
      get() {
        return this.$store.state.wholesale.receipt.dateRange
      },
      set(newValue) {
        this.$store.commit('wholesale/receipt/SET_DATE_RANGE', newValue)
      }
    },
    searchAllDate: {
      get() {
        return this.$store.state.wholesale.receipt.searchAllDate
      },
      set(newValue) {
        this.$store.commit('wholesale/receipt/SET_SEARCH_ALL_DATE', newValue)
      }
    },
    approve_status: {
      get() {
        return this.$store.state.wholesale.receipt.approve_status
      },
      set(newValue) {
        this.$store.commit('wholesale/receipt/SET_APPROVE_STATUS', newValue)
      }
    },
    branch_id: {
      get() {
        return this.$store.state.wholesale.receipt.branch_id
      },
      set(newValue) {
        this.$store.commit('wholesale/receipt/SET_BRANCH_ID', newValue)
      }
    },
    location_id: {
      get() {
        return this.$store.state.wholesale.receipt.location_id
      },
      set(newValue) {
        this.$store.commit('wholesale/receipt/SET_LOCATION_ID', newValue)
      }
    },

    columns() {
      return [
        {
          name: this.$t('wholesaleReceipt.branchName'),
          style: 'width:150px;'
        },
        { name: this.$t('wholesaleReceipt.receiptNo') },
        {
          name: this.$t('wholesaleReceipt.customer')
        },
        {
          name: this.$t('wholesaleReceipt.invoiceAmount'),
          style: 'text-align:right;'
        },
        {
          name: this.$t('wholesaleReceipt.amountPaid'),
          style: 'text-align:right;'
        },
        {
          name: this.$t('wholesaleReceipt.amountDue'),
          style: 'text-align:right;'
        },
        {
          name: this.$t('wholesaleReceipt.cashierName'),
          style: 'text-align:center;'
        },
        {
          name: this.$t('wholesaleReceipt.status'),
          style: 'text-align:center;'
        },
        {
          name: this.$t('wholesaleReceipt.approvedBy'),
          style: 'text-align:center;'
        },
        {
          name: this.$t('actions'),
          style: 'text-align:center; width:7%'
        }
      ]
    }
  },
  methods: {
    ...mapActions('report/resource', ['getLocation']),
    moment,
    orgPaymentDate(org_payment_date) {
      if (org_payment_date) {
        return moment(org_payment_date).format('DD-MM-YYYY')
      }
      return ''
    },
    totalInvoice(record) {
      let invoice_amount = _.sumBy(record.children, 'invoice_amount')
      if (invoice_amount) {
        return this.$defaultCurrencyWithCode(_.round(invoice_amount, 2))
      }
      return this.$defaultCurrencyWithCode(parseFloat(invoice_amount).toFixed(2))
    },
    totalAmountPaid(record) {
      let amount_paid = _.sumBy(record.children, 'amount_paid')
      if (amount_paid) {
        return this.$defaultCurrencyWithCode(_.round(amount_paid, 2))
      }

      return this.$defaultCurrencyWithCode(parseFloat(amount_paid).toFixed(2))
    },
    totalAmountDue(record) {
      let amount_due = _.sumBy(record.children, 'amount_due')
      if (amount_due) {
        return this.$defaultCurrencyWithCode(_.round(amount_due, 2))
      }
      return this.$defaultCurrencyWithCode(parseFloat(amount_due).toFixed(2))
    },
    addNew() {
      this.$router.push({ name: 'wholesale-receipt-create' })
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('wholesale/receipt/fetch', { ...attributes })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    deleteRecord(record) {
      this.model.deleted_reason = null
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        content: h =>
          h(Reason, {
            props: {
              value: this.model,
              validate: this.errors,
              placeholder: this.$t('wholesale.leaveReason')
            }
          }),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        onOk: () => {
          return this.$store
            .dispatch('wholesale/receipt/destroy', {
              id: record.receipt_id,
              data: this.model
            })
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              this.errors = new Errors(error.errors)
              return Promise.reject(error)
            })
        },
        onCancel() { }
      })
    },
    confirm(record, type) {
      this.confirm_type = type
      // this.$store.commit('supplier/profile/SET_EDIT_DATA', record)
      this.confirmData = record
      this.showForm = true
    },
    receiptPayment(record) {
      this.wholesalereceipt = true
      this.src = ''
      this.receipt_data = record
    },
    onCancel() {
      this.fetchData()
      this.wholesalereceipt = false
      this.showForm = false
    },
    onShowExport() {
      this.show_export_form = true
    },
    onExport(attributes) {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('wholesale/receipt/exportExel', { ...attributes })
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
          this.show_export_form = false
          this.clearInput()
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.exporting = false
        })
    },
    reprintReport(record) {
      this.show_pdf = true
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('wholesale/receipt/reprint', record.receipt_id)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    filterBranch(value) {
      this.$store.commit('wholesale/receipt/SET_LOCATION_ID', [])
      this.changeBranch(value)
      this.$store.commit('wholesale/receipt/SET_BRANCH_ID', value)
      this.fetchData({
        search: this.search
      })
    },
    getBranch() {
      this.loading = true
      this.$store
        .dispatch('security/user/getBranchAssignedForFilter')
        .then(response => {
          this.branchs = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async changeBranch() {
      this.loading = true
      this.location_id = []
      await this.$store
        .dispatch('security/user/getWarehouseAssignedForFilter', {
          branch_id: this.branch_id
        })
        .then(response => {
          this.locations = response.data.map(el => ({
            value: el.location_id,
            label: el.location_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500),
    approve_status: debounce(function () {
      this.fetchData()
    }, 500),
    location_id: debounce(function () {
      this.fetchData(this.search)
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('wholesale/receipt/RESET_STATE')
    next()
  }
}
</script>
